import React from 'react';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import I18n from 'i18n-js';

import { CoreSiteBannerHero, SiteBanner, core_apimessages_ContentSponsorship_SponsorshipType } from '../../schemas/rql';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import SearchHeader from '@reverbdotcom/commons/src/cms/headers/search_header';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import AdTag from '@reverbdotcom/commons/src/components/ads/ad_tag';
import experiments from '@reverbdotcom/commons/src/experiments';

const COMPONENT_NAME = 'SiteBannerHero';
const DEFAULT_SEARCH_HEADER = 'SearchHeader';

interface ContentSponsorshipProps {
  contentSponsorship: CoreSiteBannerHero.ContentSponsorship
}

interface UserProps {
  user: Partial<IUser>;
}

function buildClickEvent(siteBanner: SiteBanner) {
  return {
    eventName: MParticleEventName.SiteBanner,
    componentName: COMPONENT_NAME,
    entityId: siteBanner.id,
    isAd: !!siteBanner.contentSponsorship,
    isDefault: false,
  };
}

function PartnershipSponsorshipContent({ contentSponsorship }: ContentSponsorshipProps) {
  if (
    contentSponsorship?.sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.PARTNERSHIP &&
      contentSponsorship?.partnershipName) {
    return (
      <p className="size-80 mb-1 style-italic">
        {I18n.t('discovery.contentSponsorship.partnership', { partner: contentSponsorship.partnershipName })}
      </p>
    );
  }

  return null;
}

function AdSponsorshipContent({ contentSponsorship }: ContentSponsorshipProps) {
  if (contentSponsorship?.sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.AD) {
    return (
      <div className="site-banner-hero__ad">
        <AdTag inline small />
      </div>
    );
  }

  return null;
}

function DefaultSearchHeader({ user }: UserProps) {
  const searchHeaderSubtitleText = user.loggedOut ? null : I18n.t('discovery.searchHeader.logged_in_subtitle');
  return (
    <SearchHeader
      subtitle={searchHeaderSubtitleText}
      trackSearchFn={buildTrackSearchFn(user)}
      renderSecondaryImage
      rotatingImages
    />
  );
}

function getBackgroundImageStyle(siteBanner: SiteBanner) {
  return { backgroundImage: `url(${siteBanner.imageUrl})` };
}

function buildTrackSearchFn(user) {
  return function trackSearchFn(query) {
    trackEvent(
      {
        eventName: MParticleEventName.SiteBanner,
        componentName: DEFAULT_SEARCH_HEADER,
        query,
        isDefault: true,
        experiments: formatExperimentsForEventAttributes(
          user,
          [
            experiments.UK_DEFAULT_SITEBANNER_IMAGE,
          ],
        ),
      },
    );
  };
}

export function SiteBannerHero(props: ChildProps<{}, CoreSiteBannerHero.Query>) {
  const user = useUser();
  if (props.data?.loading) {
    return null;
  }
  const { siteBanner } = props?.data;

  if (!siteBanner || !siteBanner.hasPhotos) {
    return <DefaultSearchHeader user={user} />;
  }
  const { contentSponsorship } = siteBanner;

  return (
    <div className="site-banner-hero">
      <CoreLink
        to={siteBanner.url.href}
        className="site-banner-hero__inner"
        style={getBackgroundImageStyle(siteBanner)}
        clickEvent={buildClickEvent(siteBanner)}
      >
        <div className="site-banner-hero__content">
          <PartnershipSponsorshipContent contentSponsorship={contentSponsorship} />
          <p className="site-banner-hero__subtext">
            {siteBanner.subheading}
          </p>
          <h2 className="site-banner-hero__headline">
            {siteBanner.heading}
          </h2>
          <span className="site-banner-hero__cta">
            {siteBanner.callToAction}
          </span>
        </div>
        <AdSponsorshipContent contentSponsorship={contentSponsorship} />
      </CoreLink>
    </div>
  );
}

const withQuery = graphql<CoreSiteBannerHero.Query>(
  gql`
    query Core_SiteBannerHero {
      siteBanner {
        id
        heading
        subheading
        callToAction
        url {
          href
        }
        hasPhotos
        imageUrl
        contentSponsorship {
          sponsorshipType
          partnershipName
        }
      }
    }
  `,
);

export default withQuery(SiteBannerHero);
